import type { Session } from '@cyberscore/contracts';
import { ApiError } from './api';
import { accounts } from './auth';

/**
 * Keeps the access token (decision 0033), outside React. With the API's own sign-in it lives only in
 * memory: the refresh cookie (HttpOnly, invisible to scripts) gets a new one a minute before it expires,
 * and when the page loads. Development builds can also hold a dev issuer token for the tab.
 */
export interface TokenState {
  auth: { token: string; mode: 'account' | 'dev'; /** Changes on a new sign-in, not on a refresh. */ id: number } | null; // prettier-ignore
  /** Still trying the refresh cookie from an earlier visit. */
  restoring: boolean;
}

const DEV_STORE = 'cyberscore.session';

class TokenKeeper {
  private state: TokenState;
  private readonly listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inflight: Promise<'ok' | 'none' | 'error'> | null = null;
  private ids = 1;
  private restored = false;

  constructor() {
    const dev = readDev();
    this.state = { auth: dev ? { token: dev, mode: 'dev', id: this.ids } : null, restoring: !dev };
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  get = () => this.state;

  /** Once, when the app starts: a refresh cookie from an earlier visit signs back in without a password. */
  restore() {
    if (this.restored || !this.state.restoring) return;
    this.restored = true;
    const attempt = async (n: number): Promise<void> => {
      // The server restarting (a deploy) is not a reason to sign anyone out: try again shortly.
      if ((await this.trade()) === 'error' && n < 4) {
        await new Promise((r) => setTimeout(r, 750 * 2 ** n));
        return attempt(n + 1);
      }
    };
    void attempt(0).finally(() => this.set({ ...this.state, restoring: false }));
  }

  signedIn(s: Session) {
    writeDev(null);
    this.keep(s, true);
  }

  signedInDev(token: string) {
    writeDev(token);
    this.stop();
    this.set({ auth: { token, mode: 'dev', id: ++this.ids }, restoring: false });
  }

  /** A new access token from the refresh cookie; false when the session has ended. */
  async refresh(): Promise<boolean> {
    return (await this.trade()) !== 'none';
  }

  /**
   * Trades the refresh cookie for a new access token, one tab at a time so tabs don't trip reuse detection.
   * 'none': there is no session (or it ended). 'error': the server couldn't be asked; the session may be fine.
   */
  private trade(): Promise<'ok' | 'none' | 'error'> {
    this.inflight ??= withLock(() => accounts.refresh())
      .then((s) => {
        if (!s) return 'none' as const;
        this.keep(s, false);
        return 'ok' as const;
      })
      // Only the server saying the session is over ends it. Too many requests (429), a timeout or the server
      // restarting leave the session as it is, to try again.
      .catch((e: unknown) =>
        e instanceof ApiError && (e.status === 401 || e.status === 403)
          ? ('none' as const)
          : ('error' as const),
      ) // prettier-ignore
      .finally(() => {
        this.inflight = null;
      });
    return this.inflight;
  }

  /** Signs out here; with the API's own sign-in, also ends the session on the server. */
  signOut() {
    if (this.state.auth?.mode === 'account') void accounts.signOut().catch(() => {});
    this.clear();
  }

  clear() {
    this.stop();
    writeDev(null);
    this.set({ auth: null, restoring: false });
  }

  private keep(s: Session, fresh: boolean) {
    this.stop();
    this.timer = setTimeout(() => void this.renew(), Math.max(30, s.expires_in - 60) * 1000);
    const id = fresh || !this.state.auth ? ++this.ids : this.state.auth.id;
    this.set({ auth: { token: s.access_token, mode: 'account', id }, restoring: this.state.restoring });
  }

  /** Before the token expires: a new one, or retry while the server is unreachable, or sign out. */
  private async renew(tries = 0) {
    const r = await this.trade();
    if (r === 'none') this.clear();
    else if (r === 'error' && tries < 6) this.timer = setTimeout(() => void this.renew(tries + 1), 10_000);
  }

  private stop() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private set(next: TokenState) {
    this.state = next;
    for (const fn of this.listeners) fn();
  }
}

export const tokens = new TokenKeeper();

function withLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: { request: (n: string, f: () => Promise<T>) => Promise<T> } }).locks; // prettier-ignore
  return locks ? locks.request('cyberscore-refresh', fn) : fn();
}

function readDev(): string | null {
  // Development sign-in only: production builds never keep a token in storage.
  if (!import.meta.env.DEV) return null;
  try {
    const raw = sessionStorage.getItem(DEV_STORE);
    return raw ? ((JSON.parse(raw) as { token?: string }).token ?? null) : null;
  } catch {
    return null;
  }
}

function writeDev(token: string | null) {
  try {
    if (token) sessionStorage.setItem(DEV_STORE, JSON.stringify({ token }));
    else sessionStorage.removeItem(DEV_STORE);
  } catch {
    /* storage unavailable */
  }
}
